<script setup lang="ts">
  import UserAvatar from '@/components/app/users/UserAvatar.vue'
  import PDFViewer from '@/components/pdf/pdfViewer.vue'
  import {
    AugmentationVariantEnum,
    type CustomerCaseDataChange as CaseDataChange,
    ChangeSourceEnum,
    type CustomerEntityChange as EntityChange,
    type ProposedEntityChange
  } from '@/types/generated/types.gen'
  import {
    Calendar,
    FileText,
    FileX,
    Mail,
    MessageCircleQuestion,
    MessageSquare,
    Phone,
    User
  } from 'lucide-vue-next'
  import { computed, ref, type PropType } from 'vue'
  import { useTranslation } from '@/composables/useTranslation'

  const props = defineProps({
    change: {
      type: Object as PropType<EntityChange | CaseDataChange | null>,
      required: false
    },
    proposedChange: {
      type: Object as PropType<ProposedEntityChange | null>,
      required: false,
      default: () => null
    },
    disablePdfViewer: {
      type: Boolean,
      default: false
    }
  })

  const emit = defineEmits(['openPdf'])

  const { t } = useTranslation({
    en: {
      questionnaire: 'Questionnaire',
      appointment: 'Appointment Booking',
      pdfForm: 'PDF Form',
      ai: 'AI',
      email: 'E-Mail',
      call: 'Call',
      chatbot: 'Chatbot',
      system: 'SYSTEM',
      page: 'Page',
      deletedDocument: '[DELETED]'
    },
    de: {
      questionnaire: 'Fragebogen',
      appointment: 'Terminbuchung',
      pdfForm: 'PDF Formular',
      ai: 'KI',
      email: 'E-Mail',
      call: 'Anruf',
      chatbot: 'Chatbot',
      system: 'SYSTEM',
      page: 'Seite',
      deletedDocument: '[GELÖSCHT]'
    }
  })

  const isPdfOpen = ref(false)
  const pdfUrl = ref('')
  const pdfFilename = ref('')
  const pdfInitialPage = ref(1)
  const pdfDocument = ref(null)

  const initialPage = computed(() => {
    const pages = props.change?.ai_augmentation?.referenced_document_pages
    if (pages && pages.length > 0) {
      return pages[0]
    }
    return 1
  })
  const aiAugmentationDocument = computed(() => {
    if (!props.change?.ai_augmentation) {
      return null
    }
    return props.change.ai_augmentation.document || null
  })

  const isDocumentDeleted = computed(() => {
    return aiAugmentationDocument.value?.is_deleted
  })

  const shortenDocumentTitle = (title: string) => {
    if (title.length > 20) {
      return title.slice(0, 12) + '[…].pdf'
    }
    return title
  }

  const documentDisplayTitle = computed(() => {
    if (aiAugmentationDocument.value?.filename) {
      return shortenDocumentTitle(aiAugmentationDocument.value.filename)
    }
    if (aiAugmentationDocument.value?.title) {
      return shortenDocumentTitle(aiAugmentationDocument.value.title)
    }
    return null
  })

  const deletedDocumentDisplayTitle = computed(() => {
    if (isDocumentDeleted.value) {
      return t('deletedDocument') + ' ' + documentDisplayTitle.value
    }
    return null
  })

  // Proposed change document helpers
  const proposedDocumentTitle = computed(() => {
    const title = props.proposedChange?.source_document?.title
    return title ? shortenDocumentTitle(title) : null
  })

  const isProposedDocumentDeleted = computed(() => {
    return props.proposedChange?.source_document?.is_deleted
  })

  function openPdf() {
    if (props.disablePdfViewer) {
      emit('openPdf', props.change)
      return
    }

    const document = props.change?.ai_augmentation?.document
    if (document) {
      pdfUrl.value = document.url
      pdfFilename.value = document.filename || ''
      pdfDocument.value = document
      const pages = props.change?.ai_augmentation?.referenced_document_pages
      pdfInitialPage.value = pages && pages.length > 0 ? pages[0] : 1
      isPdfOpen.value = true
    }
  }

  function openProposedPdf() {
    if (props.disablePdfViewer) {
      emit('openPdf', props.proposedChange)
      return
    }

    const document = props.proposedChange?.source_document
    if (document?.current_pdf_url) {
      pdfUrl.value = document.current_pdf_url
      pdfFilename.value = document.title || ''
      pdfDocument.value = null
      pdfInitialPage.value = 1
      isPdfOpen.value = true
    }
  }
</script>
<template>
  <div class="flex flex-col gap-1">
    <!-- Regular Change Source -->
    <div v-if="change">
      <!-- User -->
      <span
        v-if="change.change_source === ChangeSourceEnum.USER"
        class="flex items-center gap-2"
        :class="proposedChange ? 'text-neutral-500 line-through' : ''"
      >
        <template v-if="change.user">
          <UserAvatar
            :user="change.user"
            :size="proposedChange ? '20px' : '26px'"
            :deactivated="!!proposedChange"
          />
          {{ change.user.first_name }} {{ change.user.last_name }}
        </template>
        <User
          v-else
          class="h-4 w-4"
        />
      </span>
      <!-- Questionnaire -->
      <span
        v-else-if="change.change_source === ChangeSourceEnum.QUESTIONNAIRE"
        class="flex items-center gap-2"
        :class="proposedChange ? 'text-neutral-500 line-through' : 'text-neutral-800'"
      >
        <MessageCircleQuestion class="h-4 w-4" />
        {{ t('questionnaire') }}
      </span>
      <!-- Appointment -->
      <span
        v-else-if="change.change_source === ChangeSourceEnum.APPOINTMENT"
        class="flex items-center gap-2"
        :class="proposedChange ? 'text-neutral-500 line-through' : 'text-neutral-800'"
      >
        <Calendar class="h-4 w-4" />
        {{ t('appointment') }}
      </span>
      <span
        v-else-if="change.change_source === ChangeSourceEnum.PDF_FORM"
        class="flex items-center gap-2"
        :class="proposedChange ? 'text-neutral-500 line-through' : 'text-neutral-800'"
      >
        <FileText class="h-4 w-4" />
        {{ t('pdfForm') }}
      </span>
      <!-- AI -->
      <div
        v-else-if="change.change_source === ChangeSourceEnum.AI"
        class="flex items-center gap-2"
      >
        <span class="flex items-center gap-2">
          <!-- Document Analysis -->
          <button
            v-if="
              change.ai_augmentation?.augmentation_variant ===
              AugmentationVariantEnum.DOCUMENT_ANALYSIS
            "
            class="group inline-flex cursor-pointer items-center space-x-1 underline"
            :class="{
              'hover:no-underline': isDocumentDeleted,
              'hover:text-purple-700': !isDocumentDeleted,
              'text-neutral-800': !proposedChange,
              'text-neutral-500 line-through hover:[text-decoration-line:underline]': proposedChange
            }"
            @click="!isDocumentDeleted ? openPdf() : null"
          >
            <FileX
              v-if="isDocumentDeleted"
              class="h-4 w-4 cursor-default text-neutral-600"
            />
            <FileText
              v-else
              class="h-4 w-4 text-neutral-600"
              :class="
                !isDocumentDeleted
                  ? 'group-hover:text-purple-700'
                  : 'cursor-default text-neutral-600'
              "
            />
            <span :class="isDocumentDeleted ? 'cursor-default text-neutral-600' : ''">
              <span v-if="!isDocumentDeleted">
                {{ documentDisplayTitle }}
              </span>
              <span v-else>
                {{ deletedDocumentDisplayTitle }}
              </span>
              <span
                v-for="page in change.ai_augmentation?.referenced_document_pages"
                :key="page"
                class="ml-1 inline-flex items-center justify-center rounded-full bg-purple-100 px-2 py-0.5 text-xs
                  font-medium text-purple-700"
                :title="`${t('page')} ${page}`"
              >
                {{ t('page').charAt(0) }}. {{ page }}
              </span>
            </span>
          </button>
          <!-- Email Extraction -->
          <template
            v-else-if="
              change.ai_augmentation?.augmentation_variant ===
              AugmentationVariantEnum.EMAIL_EXTRACTION
            "
          >
            <Mail class="h-4 w-4" />
            {{ t('email') }}
          </template>
          <!-- Call -->
          <template
            v-else-if="
              change.ai_augmentation?.augmentation_variant === AugmentationVariantEnum.CALL
            "
          >
            <Phone class="h-4 w-4" />
            {{ t('call') }}
          </template>
          <!-- Chatbot -->
          <template
            v-else-if="
              change.ai_augmentation?.augmentation_variant === AugmentationVariantEnum.CHATBOT
            "
          >
            <MessageSquare class="h-4 w-4" />
            {{ t('chatbot') }}
          </template>
        </span>
        <!-- AI Badge (shown for all AI augmentations) -->
        <span
          v-if="change.ai_augmentation"
          class="flex h-5 w-5 items-center justify-center rounded-sm bg-purple-200 p-1 text-xs font-semibold
            text-purple-700"
        >
          {{ t('ai') }}
        </span>
      </div>
      <span
        v-else-if="change.change_source === ChangeSourceEnum.SYSTEM"
        :class="proposedChange ? 'text-neutral-500 line-through' : 'text-neutral-800'"
      >
        {{ t('system') }}
      </span>
    </div>

    <!-- Proposed Change Source -->
    <div
      v-if="proposedChange"
      class="flex items-center gap-2"
    >
      <!-- USER -->
      <span
        v-if="proposedChange.change_source === ChangeSourceEnum.USER"
        class="flex items-center gap-2 text-neutral-800"
      >
        <template v-if="proposedChange.user">
          <UserAvatar
            :user="proposedChange.user"
            size="20px"
          />
          {{ proposedChange.user.first_name }} {{ proposedChange.user.last_name }}
        </template>
        <User
          v-else
          class="h-4 w-4"
        />
      </span>
      <!-- QUESTIONNAIRE -->
      <span
        v-else-if="proposedChange.change_source === ChangeSourceEnum.QUESTIONNAIRE"
        class="flex items-center gap-2 text-neutral-800"
      >
        <MessageCircleQuestion class="h-4 w-4" />
        {{ t('questionnaire') }}
      </span>
      <!-- APPOINTMENT -->
      <span
        v-else-if="proposedChange.change_source === ChangeSourceEnum.APPOINTMENT"
        class="flex items-center gap-2 text-neutral-800"
      >
        <Calendar class="h-4 w-4" />
        {{ t('appointment') }}
      </span>
      <!-- PDF_FORM -->
      <span
        v-else-if="proposedChange.change_source === ChangeSourceEnum.PDF_FORM"
        class="flex items-center gap-2 text-neutral-800"
      >
        <FileText class="h-4 w-4" />
        {{ t('pdfForm') }}
      </span>
      <!-- AI (including fallback for older proposed changes without change_source) -->
      <template v-else>
        <button
          v-if="proposedChange.source_document"
          class="group inline-flex cursor-pointer items-center space-x-1 text-neutral-800"
          :class="!isProposedDocumentDeleted ? 'underline hover:text-purple-700' : ''"
          @click="!isProposedDocumentDeleted ? openProposedPdf() : null"
        >
          <FileText
            class="h-4 w-4"
            :class="
              !isProposedDocumentDeleted
                ? 'text-neutral-600 group-hover:text-purple-700'
                : 'cursor-default text-neutral-600'
            "
          />
          <span :class="isProposedDocumentDeleted ? 'cursor-default text-neutral-600' : ''">
            <span v-if="!isProposedDocumentDeleted">
              {{ proposedDocumentTitle }}
            </span>
            <span v-else>{{ t('deletedDocument') }} {{ proposedDocumentTitle }}</span>
          </span>
        </button>
        <span
          class="flex h-5 w-5 items-center justify-center rounded-sm bg-purple-200 p-1 text-xs font-semibold
            text-purple-700"
        >
          {{ t('ai') }}
        </span>
      </template>
    </div>

    <!-- Single PDF Viewer for both regular changes and proposed changes -->
    <PDFViewer
      v-if="!disablePdfViewer"
      v-model:isOpen="isPdfOpen"
      v-model:url="pdfUrl"
      :filename="pdfFilename"
      :initial-page="pdfInitialPage"
      :scale="1.0"
      :document="pdfDocument"
    />
  </div>
</template>
